import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import maplibregl, { Map as MapLibreMap, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin, Truck, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { isSupportedLocale } from '../i18n';

interface TrackPayload {
  vehicleName: string;
  position: { latitude: number; longitude: number; updatedAt: string } | null;
  speed: number | null;
  heading: number | null;
  expiresAt: string;
  /** Tenant's language code — when present, overrides browser detection so
   *  the customer sees the page in the same language as the SMS/email they
   *  received from the dispatcher. */
  tenantLanguage?: string | null;
  mission: {
    reference: string;
    status: string;
    destination: { latitude: number; longitude: number; address: string | null } | null;
    scheduledAt: string | null;
  } | null;
}

const POLL_INTERVAL_MS = 15_000;
const API_URL = import.meta.env.VITE_API_URL || '';

function haversineKm(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export default function TrackPage() {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();

  function formatTimeRemaining(target: Date): string {
    const diffMs = target.getTime() - Date.now();
    if (diffMs <= 0) return t('track.expired');
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return t('track.less_than_a_minute');
    if (min < 60) return `${min} ${t('common.minutes_short')}`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h} ${t('common.hours_short')}` : `${h} ${t('common.hours_short')} ${m} ${t('common.minutes_short')}`;
  }

  const [data, setData] = useState<TrackPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const vehicleMarkerRef = useRef<Marker | null>(null);
  const destMarkerRef = useRef<Marker | null>(null);
  const initialCenteredRef = useRef(false);

  // Poll the public endpoint
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchOnce = async () => {
      try {
        const res = await axios.get<TrackPayload>(`${API_URL}/api/public/track/${encodeURIComponent(token)}`, {
          // No credentials, no auth — this is fully public
          withCredentials: false,
        });
        if (cancelled) return;
        setData(res.data);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError(t('track.link_invalid_or_expired'));
        } else if (axios.isAxiosError(err) && err.response?.status === 429) {
          setError(t('track.rate_limited'));
        } else {
          setError(t('track.fetch_error'));
        }
        setLoading(false);
      } finally {
        if (!cancelled) timer = setTimeout(fetchOnce, POLL_INTERVAL_MS);
      }
    };

    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, t]);

  // Adopt tenant language as soon as it arrives in the payload, so the
  // page mirrors the SMS/email language regardless of the customer's browser.
  useEffect(() => {
    const lang = data?.tenantLanguage;
    if (lang && isSupportedLocale(lang) && i18n.resolvedLanguage !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [data?.tenantLanguage, i18n]);

  // Tick every 15s for "updated X min ago" / "expires in X min" labels
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // Initialize / update map
  useEffect(() => {
    if (!data || !data.position || !mapContainerRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new maplibregl.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: [
                'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
              ],
              tileSize: 256,
              attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            },
          },
          layers: [{ id: 'osm-layer', type: 'raster', source: 'osm-tiles' }],
        },
        center: [data.position.longitude, data.position.latitude],
        zoom: 13,
        attributionControl: { compact: true },
      });
      mapRef.current.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      // The container size may not be settled on the first effect run.
      // Force a resize once the map is loaded so tiles render at the right scale.
      mapRef.current.on('load', () => mapRef.current?.resize());
      // And again on the next animation frame, to catch late layout shifts.
      requestAnimationFrame(() => mapRef.current?.resize());
    }

    // Vehicle marker
    const lngLat: [number, number] = [data.position.longitude, data.position.latitude];
    if (!vehicleMarkerRef.current) {
      const el = document.createElement('div');
      el.style.cssText =
        'width:36px;height:36px;border-radius:50%;background:#1f8da1;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-size:18px';
      el.textContent = '🚚';
      vehicleMarkerRef.current = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(mapRef.current);
    } else {
      vehicleMarkerRef.current.setLngLat(lngLat);
    }

    // Destination marker (if mission)
    if (data.mission?.destination) {
      const dest = data.mission.destination;
      const destLngLat: [number, number] = [dest.longitude, dest.latitude];
      if (!destMarkerRef.current) {
        const el = document.createElement('div');
        el.style.cssText =
          'width:32px;height:32px;border-radius:50% 50% 50% 0;background:#ef4444;border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.3)';
        destMarkerRef.current = new maplibregl.Marker({ element: el, anchor: 'bottom' }).setLngLat(destLngLat).addTo(mapRef.current);
      } else {
        destMarkerRef.current.setLngLat(destLngLat);
      }
    } else if (destMarkerRef.current) {
      destMarkerRef.current.remove();
      destMarkerRef.current = null;
    }

    // First time only: fit to vehicle + destination if both present
    if (!initialCenteredRef.current) {
      initialCenteredRef.current = true;
      if (data.mission?.destination) {
        const bounds = new maplibregl.LngLatBounds(lngLat, lngLat);
        bounds.extend([data.mission.destination.longitude, data.mission.destination.latitude]);
        mapRef.current.fitBounds(bounds, { padding: 80, maxZoom: 18, duration: 0 });
      } else {
        mapRef.current.setCenter(lngLat);
        mapRef.current.setZoom(18);
      }
    } else {
      // Subsequent updates: ease-pan to new vehicle position without zooming
      mapRef.current.easeTo({ center: lngLat, duration: 800 });
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Compute distance to destination
  const distanceKm =
    data?.position && data?.mission?.destination
      ? haversineKm(data.position, data.mission.destination)
      : null;

  const expiresDate = data ? new Date(data.expiresAt) : null;
  const positionDate = data?.position ? new Date(data.position.updatedAt) : null;
  const positionAgeMin = positionDate
    ? Math.max(0, Math.floor((Date.now() - positionDate.getTime()) / 60_000))
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          {t('track.loading')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('track.link_unavailable')}</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f9fafb',
      }}
    >
      {/* Header */}
      <header
        style={{ flexShrink: 0 }}
        className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center text-white">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900">{data.vehicleName}</h1>
            {data.mission && (
              <p className="text-xs text-gray-500">{t('track.mission_label', { ref: data.mission.reference })}</p>
            )}
          </div>
        </div>
        {expiresDate && (
          <div className="text-right">
            <div className="text-xs text-gray-500 flex items-center gap-1 justify-end">
              <Clock className="w-3 h-3" />
              {t('track.expires_in')}
            </div>
            <div className="text-sm font-medium text-gray-900">{formatTimeRemaining(expiresDate)}</div>
          </div>
        )}
      </header>

      {/* Map */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <div
          ref={mapContainerRef}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
        {!data.position && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
            <div className="bg-white rounded-xl shadow p-6 text-center">
              <MapPin className="w-10 h-10 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600">{t('track.position_unavailable')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer info bar */}
      <footer
        style={{ flexShrink: 0 }}
        className="bg-white border-t border-gray-200 px-4 py-3"
      >
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-gray-600">
            {positionAgeMin !== null && (
              <span title={positionDate?.toLocaleString(i18n.resolvedLanguage || 'fr')}>
                {positionAgeMin === 0
                  ? t('track.updated_just_now')
                  : t('track.updated_ago', { minutes: positionAgeMin })}
              </span>
            )}
            {data.speed !== null && (
              <span className="text-gray-500">{Math.round(data.speed)} {t('common.kmh')}</span>
            )}
          </div>
          {distanceKm !== null && (
            <div className="font-semibold text-cyan-700">
              {distanceKm < 1
                ? `${Math.round(distanceKm * 1000)} ${t('common.meters')}`
                : `${distanceKm.toFixed(1)} ${t('common.kilometers')}`}
            </div>
          )}
        </div>
        {data.mission?.destination?.address && (
          <p className="text-xs text-gray-500 mt-2 truncate">{t('track.destination_arrow', { address: data.mission.destination.address })}</p>
        )}
      </footer>
    </div>
  );
}
